import { computed, ref } from 'vue';
import { useApplicationEvents } from '../use-events/useEvents';

export interface ApplicationConfig {
  apiUrl: string;
  alternativeApiUrl?: string;
  /** Optional mapping of hostname -> apiUrl, used to resolve the effective apiUrl dynamically based on the current window.location.hostname */
  apiUrlByHost?: Record<string, string>;
  maxFailedFetchAttempts: number;
  highlightTableRows: boolean;
  /** the desired firing rate for event ticker */
  firingRate: number;
  /** Interval to fetch alerts (alertsJson) from the backend in millis */
  alertsFetchInterval: number;
  /** Interval to fetch the alert stats from the backend*/
  statsRefreshInterval: number;
  serviceTypesRefreshInterval: number;
  alertsPerCountryRefreshInterval: number;
  loadMapstackLabels: boolean;
  infoText?: string;
}

export const defaultApplicationConfig: ApplicationConfig = {
  apiUrl: 'https://api.sicherheitstacho.eu',
  alternativeApiUrl: undefined,
  apiUrlByHost: undefined,
  maxFailedFetchAttempts: 5,
  highlightTableRows: true,
  firingRate: 800,
  alertsFetchInterval: 25 * 1000,
  alertsPerCountryRefreshInterval: 25 * 1000,
  statsRefreshInterval: 25 * 1000,
  serviceTypesRefreshInterval: 25 * 1000,
  loadMapstackLabels: false,
  infoText: 'Visit us on Twitter: <a href="https://x.com/DTCERT">x.com/DTCERT</a>',
};

/**
 * The RuntimeConfig is the config that is used at runtime. It is a combination of the default config and the config that is loaded from the backend.
 * Moreover, it contains the information if the alternative backend is used.
 * @name RuntimeConfig
 * @description The runtime config of the application
 */
export interface RuntimeConfig extends ApplicationConfig {
  usesAlternativeBackend: boolean;
}

const defaultState: RuntimeConfig = {
  ...defaultApplicationConfig,
  usesAlternativeBackend: false,
};

const state = ref<RuntimeConfig>(defaultState);

/**
 * @name resolveApiUrl
 * @description Resolves the effective apiUrl for the given hostname. If the hostname has an
 * entry in `config.apiUrlByHost`, that value is returned; otherwise `config.apiUrl` is used.
 */
export const resolveApiUrl = (
  hostname: string,
  config: Pick<ApplicationConfig, 'apiUrl' | 'apiUrlByHost'>,
): string => {
  return config.apiUrlByHost?.[hostname] ?? config.apiUrl;
};

/**
 * @name useConfig
 * @description Composable to manage the application config
 * @returns {Object} Object with config and methods to manipulate it
 */
export const useConfig = () => {
  const setConfig = (config: Partial<ApplicationConfig>) => {
    state.value = { ...state.value, ...config };
  };
  const loadConfig = async () => {
    try {
      const result: Response = await fetch('/config.json');
      if (result.ok) {
        const payload = await result.json();
        console.log('loaded config from remote', payload);
        // remove all fields that are not in the ApplicationConfig
        const sanitizedPayload = { ...payload };
        for (const key in sanitizedPayload) {
          if (!(key in defaultApplicationConfig)) {
            delete sanitizedPayload[key];
          }
        }
        // copy all fields from the sanitized payload to the state
        state.value = Object.assign({ ...defaultState }, sanitizedPayload);
        state.value.apiUrl = resolveApiUrl(window.location.hostname, state.value);
      } else {
        console.warn('failed to load config from remote, falling back to default values');
        state.value = { ...defaultState };
      }
    } catch (error) {
      console.warn('failed to load config from remote, falling back to default values');
      state.value = { ...defaultState };
    }
  };
  const switchToAlternativeBackend = () => {
    if (state.value.usesAlternativeBackend) {
      return;
    }
    if (state.value.alternativeApiUrl) {
      console.info('switch to alternative backend', state.value.alternativeApiUrl);
    } else {
      console.info('no alternative api url is configured');
      return;
    }
    config.value.usesAlternativeBackend = true;
    const { backendChanged } = useApplicationEvents();
    backendChanged.emit();
  };
  const switchToOriginalBackend = () => {
    config.value.usesAlternativeBackend = false;
    const { backendChanged } = useApplicationEvents();
    backendChanged.emit();
  };
  const config = computed(() => state.value as RuntimeConfig);
  return {
    config,
    loadConfig,
    setConfig,
    switchToAlternativeBackend,
    switchToOriginalBackend,
  };
};
